import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { fetchDashboardSubjectDetail } from "../api/dashboardResults";
import PatientAdherenceOverlay from "../components/patients/profile/PatientAdherenceOverlay";
import PatientDemographicsOverlay from "../components/patients/profile/PatientDemographicsOverlay";
import PatientProfileHeader from "../components/patients/profile/PatientProfileHeader";
import PatientProfileLoadingShell from "../components/patients/profile/PatientProfileLoadingShell";
import PatientMeasurementsTable from "../components/patients/profile/PatientMeasurementsTable";
import PatientQuestionnaireTable from "../components/patients/profile/PatientQuestionnaireTable";
import PatientMeasurementTrendsOverlay from "../components/results/PatientMeasurementTrendsOverlay";
import PatientRecordingOverlay from "../components/results/PatientRecordingOverlay";
import { LABELLING } from "../config/labelling";
import { useAuth } from "../context/AuthContext";
import { useSubjectDemographics } from "../hooks/useSubjectDemographics";
import { useWording } from "../hooks/useWording";
import {
  canViewDashboardResults,
  canViewDashboardSubjects,
} from "../utils/authentication/dashboardPermissions";
import {
  buildQuestionnaireHistoryEntries,
  countQuestionnaireEntries,
} from "../utils/patientProfileData";
import {
  hasVisibleMeasurementTrendFeatures,
  resolveProductPresentationDashboardResultFeatures,
} from "../utils/patientPresentationFeatures";
import {
  buildSelectedPatientKey,
  readStoredSelectedPatient,
  writeStoredSelectedPatient,
} from "../utils/patientProfileSelection";
import { getPatientClinicStudyDisplay } from "../utils/patients/patientListUtils";

// Initial measurement page size and the size of each subsequent page. The
// detail endpoint pages by offset and returns delta chunks (only the rows in
// [offset, offset + limit)) plus the total count + hasMore, so pages are
// appended rather than replacing a cumulative prefix.
const DEFAULT_MEASUREMENT_LIMIT = 200;
const MEASUREMENT_PAGE_SIZE = 200;
// Safety bound on the paging loop that pulls the full history for trends.
const MAX_MEASUREMENT_PAGES = 200;

const measurementEntryKey = (entry) => `${entry?.uid}-${entry?.name}`;

// Merge a delta page of measurements onto the already-loaded profile payload,
// keeping the rest of the payload (identity, questionnaires) untouched and
// de-duplicating in case a page overlaps rows we already hold.
function appendMeasurementPage(previousData, response) {
  if (!previousData) {
    return response;
  }

  const previousEntries = Array.isArray(previousData?.measurements?.entries)
    ? previousData.measurements.entries
    : [];
  const nextEntries = Array.isArray(response?.measurements?.entries)
    ? response.measurements.entries
    : [];

  const seen = new Set(previousEntries.map(measurementEntryKey));
  const mergedEntries = [...previousEntries];

  nextEntries.forEach((entry) => {
    const key = measurementEntryKey(entry);

    if (!seen.has(key)) {
      seen.add(key);
      mergedEntries.push(entry);
    }
  });

  return {
    ...previousData,
    measurements: {
      ...previousData.measurements,
      ...response?.measurements,
      entries: mergedEntries,
    },
  };
}

export default function PatientProfilePage({
  selectedPatient,
  setSelectedPatient,
}) {
  const { t } = useTranslation();
  // The patient-details overlay names the study/clinic and the patient itself,
  // both of which are organisation-configured terms, so its copy resolves
  // through the wording translator rather than the plain one.
  const { tw } = useWording();
  const { user, claims, accountContext, isBootstrapAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const productPresentationProfile =
    accountContext?.productPresentationProfile ||
    accountContext?.deploymentProductPresentationProfile ||
    null;
  const fallbackProductPresentationProfileId =
    LABELLING.PRODUCT_PRESENTATION_PROFILE_ID ||
    accountContext?.accountContext?.productPresentationProfileId ||
    productPresentationProfile?.id ||
    accountContext?.deploymentProductPresentationProfile?.id ||
    null;
  const dashboardResultFeatures = useMemo(
    () =>
      resolveProductPresentationDashboardResultFeatures(
        productPresentationProfile,
        {
          fallbackProfileId: fallbackProductPresentationProfileId,
          featureAccess:
            accountContext?.accountContext?.productPresentationFeatureAccess ||
            null,
        },
      ),
    [
      accountContext?.accountContext?.productPresentationFeatureAccess,
      fallbackProductPresentationProfileId,
      productPresentationProfile,
    ],
  );

  const [patientData, setPatientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeEntry, setActiveEntry] = useState(null);
  const [adherenceOpen, setAdherenceOpen] = useState(false);
  const [demographicsOpen, setDemographicsOpen] = useState(false);
  const [trendsOpen, setTrendsOpen] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingTrends, setLoadingTrends] = useState(false);

  // Keep the latest translator available to the fetch effect without making it
  // a dependency. i18next's `t` identity changes when a namespace finishes
  // loading (e.g. the async `legal` bundle) or the language changes; if the
  // patient-detail fetch re-ran on those changes it would reset the paging
  // limit back to the first page and wipe out an expanded measurement list
  // (including the full history pulled in when opening trends).
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const canViewResults = canViewDashboardResults(accountContext, {
    claims,
    email: user?.email,
    isBootstrapAdmin,
  });

  const requestedSubjectKey = searchParams.get("subjectKey")?.trim() || "";
  const requestedSid = searchParams.get("sid")?.trim() || "";

  const effectiveSelectedPatient = useMemo(() => {
    const storedSelectedPatient = readStoredSelectedPatient();

    const requestedKey =
      requestedSubjectKey && requestedSubjectKey.length > 0
        ? `subject:${requestedSubjectKey}`
        : "";

    if (requestedKey) {
      if (buildSelectedPatientKey(selectedPatient) === requestedKey) {
        return selectedPatient;
      }

      if (buildSelectedPatientKey(storedSelectedPatient) === requestedKey) {
        return storedSelectedPatient;
      }

      return {
        subjectKey: requestedSubjectKey,
        sid: requestedSid || null,
      };
    }

    if (requestedSid) {
      return { sid: requestedSid };
    }

    return selectedPatient || storedSelectedPatient || null;
  }, [requestedSid, requestedSubjectKey, selectedPatient]);

  const clinicStudySummary = getPatientClinicStudyDisplay(
    patientData || effectiveSelectedPatient,
  );

  // The demographic profile is owned by user-service and keyed by uid, so a
  // pre-created subject who has not signed up yet has nothing to show.
  const subjectUid = patientData?.uid || effectiveSelectedPatient?.uid || null;
  const canViewDemographics =
    canViewDashboardSubjects(accountContext, {
      claims,
      email: user?.email,
      isBootstrapAdmin,
    }) && Boolean(subjectUid);
  const demographics = useSubjectDemographics({
    uid: subjectUid,
    enabled: demographicsOpen,
  });

  useEffect(() => {
    if (!effectiveSelectedPatient) {
      return;
    }

    writeStoredSelectedPatient(effectiveSelectedPatient);

    const currentSelectedId = buildSelectedPatientKey(selectedPatient);
    const effectiveSelectedId = buildSelectedPatientKey(
      effectiveSelectedPatient,
    );

    if (
      setSelectedPatient &&
      effectiveSelectedId &&
      effectiveSelectedId !== currentSelectedId
    ) {
      setSelectedPatient(effectiveSelectedPatient);
    }
  }, [effectiveSelectedPatient, selectedPatient, setSelectedPatient]);

  useEffect(() => {
    setActiveEntry(null);
    setAdherenceOpen(false);
    setDemographicsOpen(false);
    setTrendsOpen(false);
  }, [
    patientData?.sid,
    patientData?.subjectKey,
    effectiveSelectedPatient?.sid,
    effectiveSelectedPatient?.subjectKey,
    effectiveSelectedPatient?.uid,
    canViewResults,
  ]);

  useEffect(() => {
    if (!effectiveSelectedPatient) {
      setPatientData(null);
      setError(null);
      setLoading(false);
      return;
    }

    if (!effectiveSelectedPatient?.sid && !effectiveSelectedPatient?.uid) {
      if (!effectiveSelectedPatient?.subjectKey) {
        setPatientData(null);
        setError(null);
        setLoading(false);
        return;
      }
    }

    const abortController = new AbortController();

    (async () => {
      setLoading(true);
      setError(null);
      // Fresh patient → start again from the first page.
      setLoadingMore(false);

      try {
        const response = await fetchDashboardSubjectDetail({
          subjectKey: effectiveSelectedPatient.subjectKey,
          sid: effectiveSelectedPatient.sid,
          uid: effectiveSelectedPatient.uid,
          measurementLimit: DEFAULT_MEASUREMENT_LIMIT,
          signal: abortController.signal,
        });

        setPatientData(response);
      } catch (e) {
        if (abortController.signal.aborted) {
          return;
        }

        const errorMessage =
          e.response?.data?.error?.message ||
          e.response?.data?.error ||
          e.message ||
          "Failed to load patient data";

        setError(
          tRef.current("profilePage.error", {
            message: errorMessage,
          }),
        );
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [effectiveSelectedPatient]);

  const loadedMeasurementCount = patientData?.measurements?.entries?.length || 0;
  const hasMoreMeasurements = Boolean(patientData?.measurements?.hasMore);

  const handleLoadMore = useCallback(async () => {
    if (
      loadingMore ||
      loadingTrends ||
      !effectiveSelectedPatient ||
      !hasMoreMeasurements
    ) {
      return;
    }

    setLoadingMore(true);

    try {
      const response = await fetchDashboardSubjectDetail({
        subjectKey: effectiveSelectedPatient.subjectKey,
        sid: effectiveSelectedPatient.sid,
        uid: effectiveSelectedPatient.uid,
        measurementLimit: MEASUREMENT_PAGE_SIZE,
        measurementOffset: loadedMeasurementCount,
      });

      setPatientData((previousData) =>
        appendMeasurementPage(previousData, response),
      );
    } catch {
      // Keep the current list on failure; the user can retry.
    } finally {
      setLoadingMore(false);
    }
  }, [
    loadingMore,
    loadingTrends,
    effectiveSelectedPatient,
    hasMoreMeasurements,
    loadedMeasurementCount,
  ]);

  const questionnaireEntries = useMemo(
    () => buildQuestionnaireHistoryEntries(patientData?.questionnaires),
    [patientData?.questionnaires],
  );

  const questionnaireCount = useMemo(
    () => countQuestionnaireEntries(patientData?.questionnaires),
    [patientData?.questionnaires],
  );

  const hasQuestionnaireTrendData = useMemo(
    () =>
      Object.values(patientData?.questionnaires || {}).some((group) =>
        Array.isArray(group?.entries)
          ? group.entries.some(
              (entry) =>
                typeof entry?.score === "number" &&
                Number.isFinite(entry.score),
            )
          : false,
      ),
    [patientData?.questionnaires],
  );
  const hasMoreQuestionnaireHistory = useMemo(
    () =>
      Object.values(patientData?.questionnaires || {}).some((group) =>
        Boolean(group?.hasMore),
      ),
    [patientData?.questionnaires],
  );

  const hasMeasurementTrendsData = Boolean(
    (patientData?.measurements?.entries?.length &&
      hasVisibleMeasurementTrendFeatures(dashboardResultFeatures)) ||
      hasQuestionnaireTrendData,
  );

  const handleOpenTrends = useCallback(async () => {
    setActiveEntry(null);
    setAdherenceOpen(false);
    setTrendsOpen(true);

    if (loadingTrends) {
      return;
    }

    const needsMeasurementTopUp = Boolean(patientData?.measurements?.hasMore);
    const needsQuestionnaireTopUp = hasMoreQuestionnaireHistory;

    // Nothing left to pull, or nothing to chart → open with what we have.
    if (
      !effectiveSelectedPatient ||
      !hasMeasurementTrendsData ||
      (!needsMeasurementTopUp && !needsQuestionnaireTopUp)
    ) {
      return;
    }

    const lookup = {
      subjectKey: effectiveSelectedPatient.subjectKey,
      sid: effectiveSelectedPatient.sid,
      uid: effectiveSelectedPatient.uid,
    };

    setLoadingTrends(true);

    try {
      // Trends need the whole history. Start from what's already loaded and
      // keep a local working copy so the loop sees fresh paging state without
      // waiting on React state updates.
      let workingData = patientData;

      // 1) Expand the questionnaire history once (small payload). Ask for the
      //    measurements we already hold so this response doesn't shrink them.
      if (needsQuestionnaireTopUp) {
        const response = await fetchDashboardSubjectDetail({
          ...lookup,
          measurementLimit:
            workingData?.measurements?.entries?.length ||
            DEFAULT_MEASUREMENT_LIMIT,
          allQuestionnaires: true,
        });

        workingData = {
          ...workingData,
          questionnaires: response?.questionnaires || workingData?.questionnaires,
        };
        setPatientData(workingData);
      }

      // 2) Page through the remaining measurements as disjoint delta chunks,
      //    appending and pushing each page to the UI as it arrives.
      let pages = 0;
      while (
        workingData?.measurements?.hasMore &&
        pages < MAX_MEASUREMENT_PAGES
      ) {
        pages += 1;

        const offset = workingData.measurements.entries?.length || 0;
        const response = await fetchDashboardSubjectDetail({
          ...lookup,
          measurementLimit: MEASUREMENT_PAGE_SIZE,
          measurementOffset: offset,
        });

        const nextData = appendMeasurementPage(workingData, response);

        // Guard against a non-advancing page (avoids an infinite loop).
        if (
          (nextData.measurements.entries?.length || 0) <=
          (workingData.measurements.entries?.length || 0)
        ) {
          break;
        }

        workingData = nextData;
        setPatientData(workingData);
      }
    } catch {
      // Keep the already loaded profile payload if a page fails and still let
      // the user open the trends overlay with the current history slice.
    } finally {
      setLoadingTrends(false);
    }
  }, [
    effectiveSelectedPatient,
    hasMeasurementTrendsData,
    hasMoreQuestionnaireHistory,
    loadingTrends,
    patientData,
  ]);

  return (
    <div className="flex h-full w-full flex-col gap-4 px-4 pb-4">
      {loading ? (
        <PatientProfileLoadingShell t={t} />
      ) : error ? (
        <div className="flex h-full w-full items-center justify-center">
          <p className="text-red-500">{error}</p>
        </div>
      ) : !patientData ? (
        <div className="flex h-full w-full items-center justify-center">
          <p>{t("profilePage.empty")}</p>
        </div>
      ) : (
        <>
          <PatientProfileHeader
            subjectLabel={
              patientData?.sid ||
              effectiveSelectedPatient?.sid ||
              effectiveSelectedPatient?.uid
            }
            clinicStudySummary={clinicStudySummary}
            measurementCount={patientData?.measurements?.count || 0}
            questionnaireCount={questionnaireCount}
            canViewAdherence={canViewResults}
            onOpenAdherence={() => {
              setActiveEntry(null);
              setTrendsOpen(false);
              setDemographicsOpen(false);
              setAdherenceOpen(true);
            }}
            canViewDemographics={canViewDemographics}
            onOpenDemographics={() => {
              setActiveEntry(null);
              setTrendsOpen(false);
              setAdherenceOpen(false);
              setDemographicsOpen(true);
            }}
            t={t}
          />

          <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-3">
            <PatientMeasurementsTable
              entries={patientData?.measurements?.entries || []}
              totalCount={patientData?.measurements?.count || 0}
              hasMore={Boolean(patientData?.measurements?.hasMore)}
              loadingMore={loadingMore}
              loadingTrends={loadingTrends}
              onLoadMore={handleLoadMore}
              canViewResults={canViewResults}
              activeEntry={activeEntry}
              canOpenTrends={hasMeasurementTrendsData}
              dashboardResultFeatures={dashboardResultFeatures}
              showEnvironmentalContext={false}
              showWeatherDetails={dashboardResultFeatures?.weatherDetails !== false}
              onOpenEntry={(selectedEntry) => {
                setAdherenceOpen(false);
                setTrendsOpen(false);
                setActiveEntry(selectedEntry);
              }}
              onOpenTrends={handleOpenTrends}
              t={t}
            />

            <div className="min-h-[360px] min-w-0 lg:col-span-1">
              <PatientQuestionnaireTable
                title={t(
                  "profilePage.questionnairesTableTitle",
                  "Questionnaires",
                )}
                count={questionnaireCount}
                entries={questionnaireEntries}
                showScores
                showEnvironmentalContext={false}
                showWeatherDetails={dashboardResultFeatures?.weatherDetails !== false}
                t={t}
              />
            </div>
          </div>

          {canViewDemographics ? (
            <PatientDemographicsOverlay
              open={demographicsOpen}
              onClose={() => setDemographicsOpen(false)}
              subjectLabel={
                patientData?.sid ||
                effectiveSelectedPatient?.sid ||
                effectiveSelectedPatient?.uid
              }
              clinicStudySummary={clinicStudySummary}
              subject={demographics.subject}
              profile={demographics.profile}
              historyFields={demographics.historyFields}
              historyTruncated={demographics.historyTruncated}
              loading={demographics.loading}
              historyLoading={demographics.historyLoading}
              error={demographics.error}
              historyError={demographics.historyError}
              onRetry={demographics.reload}
              t={tw}
            />
          ) : null}

          {canViewResults ? (
            <>
              <PatientRecordingOverlay
                entry={activeEntry}
                open={Boolean(activeEntry)}
                onClose={() => setActiveEntry(null)}
                dashboardResultFeatures={dashboardResultFeatures}
              />

              <PatientAdherenceOverlay
                currentMonthAdherence={patientData?.currentMonthAdherence}
                previousMonthAdherence={patientData?.previousMonthAdherence}
                measurementEntries={patientData?.measurements?.entries || []}
                questionnaires={patientData?.questionnaires || {}}
                showQuestionnaireScores
                open={adherenceOpen}
                onClose={() => setAdherenceOpen(false)}
                subjectLabel={
                  patientData?.sid ||
                  effectiveSelectedPatient?.sid ||
                  effectiveSelectedPatient?.uid
                }
                clinicStudySummary={clinicStudySummary}
                t={t}
              />

              <PatientMeasurementTrendsOverlay
                entries={patientData?.measurements?.entries || []}
                questionnaires={patientData?.questionnaires || {}}
                loadingHistory={loadingTrends}
                open={trendsOpen}
                onClose={() => setTrendsOpen(false)}
                dashboardResultFeatures={dashboardResultFeatures}
              />
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
